import { RECRUITMENT } from "@/constants"

export interface FormFieldOption {
  label: string
  value: string
  group?: string
}

export interface HrmFormField {
  uuid?: string
  name: string
  label: string
  field_type:
    | "text"
    | "textarea"
    | "number"
    | "select"
    | "radio"
    | "date"
    | "checkbox"
    | "file"
  options: FormFieldOption[] | null
  is_required: boolean
  order: number
  validations?: unknown
}

export interface HrmFormTemplate {
  uuid: string
  name: string
  description: string | null
  status: string | null
  job_posting: string
  fields: HrmFormField[]
}

export interface RawHrmJobPosting {
  uuid: string
  applications_count?: number
  job_types?: string[]
  company?: string | null
  created_at?: string
  updated_at?: string
  title: string
  description?: string
  requisition?: string
  location?: string
  salary_min?: string | number
  salary_max?: string | number
  benefits?: string
  requirements?: string
  qualifications?: string
  skills?: string
  education?: string
  experience?: string
  is_active?: boolean
  status?: string
  entity?: string
  position?: string
  metadata?: {
    logo?: string
    block?: "store" | "office" | string
    company?: string
    company_id?: string
    location?: string
    salary_type?: "hourly" | "monthly" | string
    job_position?: string
    recruitment_period?: string
    recruitment_quantity?: string | number
    tags?: string[]
    detail?: {
      intro?: string[]
      backgroundTitle?: string
      background?: string[]
      plusTitle?: string
      plus?: string[]
      benefitsIntro?: string
      benefits?: string[]
      notices?: string[]
      links?: { label: string; href: string }[]
      company?: string
    }
  }
  form_template?: HrmFormTemplate
}

export interface MappedPublicJob {
  uuid: string
  title: string
  company_name: string
  company_id: string
  logo?: string
  location: string
  salary_min_raw: number
  salary_max_raw: number
  salary_display: string
  salary_type: "hourly" | "monthly" | string
  created_at: string
  created_at_formatted: string
  tags: string[]
  recruitment_period: string
  recruitment_quantity: string
  experience: string
  block: "store" | "office" | string
  block_label: string
  status: string
  description_html: string
  detail?: RawHrmJobPosting["metadata"] extends { detail?: infer D }
    ? D
    : undefined
  form_template?: HrmFormTemplate
}

const JOB_TYPE_LABELS: Record<string, string> = {
  FULL_TIME: "Full-time",
  PART_TIME: "Part-time",
  SEASONAL: "Thời vụ",
  COLLABORATOR: "Cộng tác viên",
  INTERN: "Intern",
}

export function formatCurrencyVnd(value?: string | number | null): string {
  if (value === null || value === undefined || value === "") return ""
  const num =
    typeof value === "string"
      ? parseFloat(value.replace(/\s/g, "").replace(/,/g, ""))
      : value
  if (Number.isNaN(num)) return String(value)
  if (num >= 1_000_000) {
    const millions = num / 1_000_000
    return `${millions % 1 === 0 ? millions : millions.toFixed(1)} triệu`
  }
  return `${num.toLocaleString("vi-VN")} VND`
}

export function formatSalaryRange(
  min?: string | number | null,
  max?: string | number | null
): string {
  if (min && max) {
    const minNum =
      typeof min === "string"
        ? parseFloat(min.replace(/\s/g, "").replace(/,/g, ""))
        : min
    const maxNum =
      typeof max === "string"
        ? parseFloat(max.replace(/\s/g, "").replace(/,/g, ""))
        : max
    if (!Number.isNaN(minNum) && !Number.isNaN(maxNum)) {
      if (minNum >= 1_000_000 && maxNum >= 1_000_000) {
        const minM = minNum / 1_000_000
        const maxM = maxNum / 1_000_000
        return `${minM % 1 === 0 ? minM : minM.toFixed(1)} - ${
          maxM % 1 === 0 ? maxM : maxM.toFixed(1)
        } triệu`
      }
      return `${formatCurrencyVnd(minNum)} - ${formatCurrencyVnd(maxNum)}`
    }
  }
  if (min) return `Từ ${formatCurrencyVnd(min)}`
  if (max) return `Đến ${formatCurrencyVnd(max)}`
  return "Thoả thuận"
}

export function formatDateVi(dateStr?: string | null): string {
  if (!dateStr) return "-"
  const d = new Date(dateStr)
  if (Number.isNaN(d.getTime())) return dateStr
  const day = String(d.getDate()).padStart(2, "0")
  const month = String(d.getMonth() + 1).padStart(2, "0")
  const year = d.getFullYear()
  return `${day}/${month}/${year}`
}

export function mapPublicJob(raw: RawHrmJobPosting): MappedPublicJob {
  const meta = raw.metadata || {}
  const tags =
    Array.isArray(meta.tags) && meta.tags.length > 0
      ? meta.tags.filter(t => typeof t === "string" && t.trim().length > 0)
      : Array.isArray(raw.job_types)
      ? raw.job_types
          .filter(t => typeof t === "string" && t.trim().length > 0)
          .map(t => JOB_TYPE_LABELS[t] ?? t)
      : []

  const minRaw = Number(raw.salary_min ?? 0)
  const maxRaw = Number(raw.salary_max ?? 0)
  const block = meta.block || "store"
  const blockLabel =
    block === "office"
      ? "Khối Văn phòng"
      : block === "store"
      ? "Khối Cửa hàng"
      : "Khối Vận hành"

  return {
    uuid: raw.uuid,
    title: raw.title,
    company_name: meta.company || raw.company || "TUTIMI",
    company_id: meta.company_id || raw.entity || "",
    logo: meta.logo,
    location: raw.location || meta.location || "TP. HCM",
    salary_min_raw: Number.isNaN(minRaw) ? 0 : minRaw,
    salary_max_raw: Number.isNaN(maxRaw) ? 0 : maxRaw,
    salary_display: formatSalaryRange(raw.salary_min, raw.salary_max),
    salary_type:
      meta.salary_type || (maxRaw > 0 && maxRaw < 500_000 ? "hourly" : "monthly"),
    created_at: raw.created_at || "",
    created_at_formatted: formatDateVi(raw.created_at),
    tags,
    recruitment_period: formatDateVi(meta.recruitment_period),
    recruitment_quantity: String(meta.recruitment_quantity || "Nhiều vị trí"),
    experience: raw.experience || "Không yêu cầu kinh nghiệm",
    block,
    block_label: blockLabel,
    status: raw.status || "published",
    description_html: raw.description || "",
    detail: meta.detail,
    form_template: raw.form_template,
  }
}

// Fallback snapshot from live https://api.tutimi.com.vn/api/hrm/v1/recruitment/public/job-postings/
const FALLBACK_TUTIMI_JOBS: RawHrmJobPosting[] = [
  {
    uuid: "38e9f0cb-2eb5-439f-9941-87580c3f3b61",
    applications_count: 0,
    job_types: ["FULL_TIME", "PART_TIME"],
    company: "TUTIMI",
    created_at: "2026-01-02T14:50:45.790844+07:00",
    updated_at: "2026-02-26T14:33:30.405035+07:00",
    title: "Nhân Viên Phục Vụ/Pha Chế",
    description:
      '<p class="text-node"><strong>1. MÔ TẢ CÔNG VIỆC</strong></p><ul class="list-node"><li><p class="text-node">Pha chế đồ uống theo công thức có sẵn</p></li><li><p class="text-node">Thực hiện các công việc thu ngân, phục vụ (hình thức mang đi),</p></li><li><p class="text-node">Các yêu cầu khác từ quản lý</p></li></ul><p class="text-node"><strong>Ca làm việc: </strong><span style="color: rgb(0, 0, 0);">Tất cả cửa hàng<strong> TUTIMI </strong>yêu cầu thời gian ca làm cố định (trừ chi nhánh tuyển xoay ca), và cần các bạn đáp ứng </span><span style="color: rgb(255, 0, 0);"><strong><u>tối thiểu 6 ca/tuần</u></strong></span></p><p class="text-node"><strong>*Part-time ca 5 - 6 tiếng/ngày</strong></p><ul class="list-node"><li><p class="text-node"><strong>Ca sáng:</strong> 6h - 12h</p></li><li><p class="text-node"><strong>Ca chiều: </strong>12h - 18h</p></li><li><p class="text-node"><strong>Ca tối: </strong>18h - 23h (hoặc 24h tùy chi nhánh)</p></li></ul><p class="text-node"><strong>*Fulltime ca 11 - 12 tiếng/ngày</strong></p><p class="text-node"><em>(Sau thử việc sẽ chốt thời gian của Fulltime)</em></p><p class="text-node"><span style="color: rgb(0, 0, 0);"><strong>2. THU NHẬP VÀ CHÍNH SÁCH PHÚC LỢI</strong></span></p><ul class="list-node"><li><p class="text-node"><span style="color: rgb(0, 0, 0);">Tháng đầu học việc: 21k/h</span></p></li><li><p class="text-node"><span style="color: rgb(0, 0, 0);">Tháng 2,3: 22k/h</span></p></li><li><p class="text-node"><span style="color: rgb(0, 0, 0);">Tháng 4 trở đi: 26k/h</span></p></li><li><p class="text-node"><span style="color: rgb(0, 0, 0);">Thâm niên từ 1 năm trở lên: 27k/h</span></p></li></ul><p class="text-node"><span style="color: rgb(0, 0, 0);">🔥Thưởng doanh thu hằng tháng (sau học việc)</span></p><p class="text-node"><span style="color: rgb(0, 0, 0);">🔥Thưởng thâm niên, thưởng tết, lương tháng 13</span></p><p class="text-node"><span style="color: rgb(0, 0, 0);">🔥Phụ cấp 10K đối với ca tối từ 23h-24h</span></p><p class="text-node"><span style="color: rgb(0, 0, 0);">🔥Miễn phí cấp đồng phục khi đi làm</span></p><p class="text-node"><span style="color: rgb(0, 0, 0);">🔥 Không yêu cầu kinh nghiệm&nbsp;</span></p><p class="text-node"><strong>3. YÊU CẦU ỨNG VIÊN</strong></p><ul class="list-node"><li><p class="text-node"><span style="color: rgb(0, 0, 0);">Đủ 18 tuổi (theo giấy tờ)</span></p></li><li><p class="text-node"><span style="color: rgb(0, 0, 0);">Gắn bó tối thiểu 3 tháng</span></p></li><li><p class="text-node"><span style="color: rgb(0, 0, 0);">Tóc không nhuộm sáng</span></p></li><li><p class="text-node"><span style="color: rgb(0, 0, 0);">Không sơn móng tay/để móng tay quá dài</span></p></li><li><p class="text-node"><span style="color: rgb(0, 0, 0);">Trang phục đi làm: <strong>Quần tây đen</strong> + <strong>Áo đồng phục TuTiMi</strong> + <strong>Giày/dép quai hậu </strong><em>(mặc áo thun đen với trường hợp học việc chưa cấp đồng phục).</em></span></p></li></ul>',
    location: "TP. HCM",
    salary_min: "21000.00",
    salary_max: "27000.00",
    is_active: true,
    status: "published",
    metadata: {
      logo: "/images/logo-tutimi.png",
      block: "store",
      company: "TUTIMI",
      location: "TP. HCM",
      salary_type: "hourly",
      recruitment_period: "2026-02-28",
      recruitment_quantity: "30",
    },
    form_template: {
      uuid: "30cab1de-3207-48cc-ab81-882876836ed3",
      name: "Thông tin ứng tuyển",
      description: "Điền thông tin của bạn để nhà tuyển dụng có thể liên hệ.",
      status: "draft",
      job_posting: "38e9f0cb-2eb5-439f-9941-87580c3f3b61",
      fields: [
        {
          uuid: "c7c9b71d-7ddf-4e22-8d73-082f3afd378e",
          name: "fullname",
          label: "Họ và tên",
          field_type: "text",
          options: null,
          is_required: true,
          order: 1,
        },
        {
          uuid: "ac9d2dcd-f613-45f0-8124-397be90a7cf7",
          name: "date_of_birth",
          label: "Ngày sinh",
          field_type: "date",
          options: null,
          is_required: true,
          order: 2,
        },
        {
          uuid: "06e69916-6a29-49b4-85f7-6aedb4ff0cd0",
          name: "gender",
          label: "Giới tính",
          field_type: "select",
          options: [
            { label: "Nam", value: "0" },
            { label: "Nữ", value: "1" },
          ],
          is_required: true,
          order: 3,
        },
        {
          uuid: "8a41cee1-4b6a-420f-a375-84942cf2ae51",
          name: "phone",
          label: "Số điện thoại (ưu tiên có zalo, không chặn người lạ)",
          field_type: "text",
          options: null,
          is_required: true,
          order: 4,
        },
        {
          uuid: "ea120901-e420-418f-9015-670f3b83064f",
          name: "email",
          label: "Email",
          field_type: "text",
          options: null,
          is_required: true,
          order: 5,
        },
        {
          uuid: "385d0d73-4f9c-4b68-a5a9-93d157f88dcf",
          name: "current_location",
          label: "Chỗ ở hiện tại",
          field_type: "text",
          options: null,
          is_required: true,
          order: 6,
        },
        {
          uuid: "49a08405-3935-4494-ad1f-9633bc7f8f96",
          name: "preferred_store",
          label: "Khu vực đăng ký",
          field_type: "checkbox",
          options: [
            { label: "Huyện Bình Chánh", value: "0" },
            { label: "Huyện Cần Giờ", value: "1" },
            { label: "Huyện Củ Chi", value: "2" },
            { label: "Huyện Hóc Môn", value: "3" },
            { label: "Huyện Nhà Bè", value: "4" },
            { label: "Quận 1", value: "5" },
            { label: "Quận 3", value: "6" },
            { label: "Quận 4", value: "7" },
            { label: "Quận 5", value: "8" },
            { label: "Quận 6", value: "9" },
            { label: "Quận 7", value: "10" },
            { label: "Quận 8", value: "11" },
            { label: "Quận 10", value: "12" },
            { label: "Quận 11", value: "13" },
            { label: "Quận 12", value: "14" },
            { label: "Quận Bình Tân", value: "15" },
            { label: "Quận Bình Thạnh", value: "16" },
            { label: "Quận Gò Vấp", value: "17" },
            { label: "Quận Phú Nhuận", value: "18" },
            { label: "Quận Tân Bình", value: "19" },
            { label: "Quận Tân Phú", value: "20" },
            { label: "TP. Thủ Đức", value: "21" },
          ],
          is_required: true,
          order: 7,
        },
        {
          uuid: "00d1c3e3-6271-4673-b2fd-c1a9cf90152a",
          name: "shifts_per_week",
          label: "Thời gian làm việc trong tuần",
          field_type: "select",
          options: [
            { label: "Có thể làm full tuần (ưu tiên)", value: "0" },
            { label: "Có thể làm tối thiểu 6 ca/tuần", value: "1" },
          ],
          is_required: true,
          order: 8,
        },
        {
          uuid: "ceff4ab8-cd54-46a5-87b4-7794703d7a9b",
          name: "working_hours",
          label: "Khung giờ đăng ký làm việc",
          field_type: "checkbox",
          options: [
            { label: "Ca sáng: 6h-12h", value: "0" },
            { label: "Ca chiều: 12h-18h", value: "1" },
            { label: "Ca tối: 18h-23h/24h", value: "2" },
            {
              label: "Xoay ca (chỉ áp dụng với chi nhánh đăng tuyển xoay ca)",
              value: "3",
            },
          ],
          is_required: true,
          order: 9,
        },
        {
          uuid: "19c17af7-5adb-4100-93e1-4faba2509753",
          name: "work_holidays",
          label: "Bạn có thể làm được những ngày lễ, tết không?",
          field_type: "select",
          options: [
            { label: "Có", value: "0" },
            { label: "Không", value: "1" },
          ],
          is_required: true,
          order: 10,
        },
        {
          uuid: "71bf4f17-19bf-482e-b1a0-1b42eb49068c",
          name: "recruitment_source",
          label: "Bạn biết đến thông tin tuyển dụng qua đâu?",
          field_type: "select",
          options: [
            { label: "Website chính thức", value: "0" },
            { label: "Fanpage Tuyển dụng", value: "1" },
            { label: "Tiktok", value: "2" },
            { label: "Zalo", value: "3" },
            { label: "Đến trực tiếp cửa hàng xin việc", value: "4" },
          ],
          is_required: true,
          order: 11,
        },
        {
          uuid: "5ea8b17b-fed8-4ee8-9bbb-432d55f49904",
          name: "interview_location",
          label: "Bạn có thể tham gia phỏng vấn ở đâu?",
          field_type: "select",
          options: [
            { label: "Tại chi nhánh đăng ký", value: "0" },
            {
              label:
                "Tại VP TuTiMi: số 146 Bình Lợi, P. Bình Lợi Trung, TP. HCM (Bình Thạnh cũ)",
              value: "1",
            },
          ],
          is_required: true,
          order: 12,
        },
      ],
    },
  },
]

/**
 * Fetch public job postings from HRM API, filtered by company="TUTIMI" and status="published"
 */
export async function fetchPublicJobs(
  company = "TUTIMI"
): Promise<MappedPublicJob[]> {
  try {
    const res = await fetch(
      `${RECRUITMENT.API_BASE}/recruitment/public/job-postings/`,
      {
        headers: {
          Accept: "application/json",
        },
      }
    )
    if (!res.ok) {
      throw new Error(`HTTP ${res.status}`)
    }
    const json = await res.json()
    const rawList: RawHrmJobPosting[] = Array.isArray(json?.data)
      ? json.data
      : []

    const mapped = rawList
      .map(mapPublicJob)
      .filter(
        job =>
          job.status === "published" &&
          (company === "all" ||
            job.company_name.toUpperCase() === company.toUpperCase())
      )

    return mapped
  } catch (err) {
    console.warn("Fallback to cached HRM job postings:", err)
    return FALLBACK_TUTIMI_JOBS.map(mapPublicJob)
  }
}

/**
 * Fetch single public job posting detail + form_template by UUID from HRM API
 */
export async function fetchPublicJobDetail(
  uuid: string
): Promise<MappedPublicJob> {
  try {
    const res = await fetch(
      `${RECRUITMENT.API_BASE}/recruitment/public/job-postings/${uuid}`,
      {
        headers: {
          Accept: "application/json",
        },
      }
    )
    if (!res.ok) {
      throw new Error(`HTTP ${res.status}`)
    }
    const json = await res.json()
    const raw: RawHrmJobPosting = json?.data ?? json
    return mapPublicJob(raw)
  } catch (err) {
    console.warn("Fallback to cached HRM job detail:", err)
    const fallback =
      FALLBACK_TUTIMI_JOBS.find(j => j.uuid === uuid) || FALLBACK_TUTIMI_JOBS[0]
    return mapPublicJob(fallback)
  }
}

const APPLIED_JOBS_STORAGE_KEY = "job_applications"

export function getAppliedJobIds(): string[] {
  try {
    const raw = localStorage.getItem(APPLIED_JOBS_STORAGE_KEY)
    return raw ? JSON.parse(raw) : []
  } catch {
    return []
  }
}

export function markJobAsApplied(jobUuid: string): void {
  try {
    const current = getAppliedJobIds()
    if (!current.includes(jobUuid)) {
      localStorage.setItem(
        APPLIED_JOBS_STORAGE_KEY,
        JSON.stringify([...current, jobUuid])
      )
    }
  } catch {
    // ignore storage errors
  }
}

export interface SubmitApplicationParams {
  jobUuid: string
  companyName?: string
  fields: HrmFormField[]
  values: Record<string, any>
}

/**
 * Submit job application directly to HRM API (matching hrm.tutimi.com.vn/$jobId behavior)
 */
export async function submitJobApplication({
  jobUuid,
  companyName = "TUTIMI",
  fields,
  values,
}: SubmitApplicationParams): Promise<void> {
  const answers = Object.entries(values).map(([name, value]) => {
    const fieldDef = fields.find(f => f.name === name)
    return {
      name,
      field_type: fieldDef?.field_type,
      value,
      label: fieldDef?.label,
      options: fieldDef?.options,
    }
  })

  const appRes = await fetch(
    `${RECRUITMENT.API_BASE}/recruitment/applications`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json;charset=UTF-8",
        Accept: "application/json, text/plain, */*",
      },
      body: JSON.stringify({
        job_posting: jobUuid,
        status: "SUB",
        answers,
      }),
    }
  )

  if (!appRes.ok) {
    const errText = await appRes.text().catch(() => "")
    throw new Error(
      errText || `Không thể gửi hồ sơ ứng tuyển (Mã lỗi ${appRes.status})`
    )
  }

  const appJson = await appRes.json()
  const appData = appJson?.data ?? appJson
  const candidateUuid = appData?.candidate?.uuid

  if (candidateUuid) {
    const additionalData = {
      company: companyName,
      gender: values.gender ?? undefined,
      date_of_birth: String(values.birth_date ?? values.date_of_birth ?? ""),
      address: values.current_location ?? undefined,
      preferred_store: values.preferred_store ?? [],
      stores: values.stores ?? [],
      shifts_per_week: String(values.shifts_per_week ?? ""),
      working_hours: values.working_hours ?? [],
      recruitment_source: String(values.recruitment_source ?? ""),
      interview_location: values.interview_location ?? undefined,
      work_holidays: values.work_holidays ?? undefined,
    }

    await fetch(
      `${RECRUITMENT.API_BASE}/recruitment/candidates/${candidateUuid}`,
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json;charset=UTF-8",
          Accept: "application/json, text/plain, */*",
        },
        body: JSON.stringify({
          fullname: values.fullname,
          email: values.email,
          phone: values.phone,
          additional_data: additionalData,
        }),
      }
    ).catch(err => {
      console.warn("Candidate patch warning:", err)
    })
  }

  markJobAsApplied(jobUuid)
}

